// Google Auth Button Component
// Location: /src/modules/googleAds/components/GoogleAuthButton.tsx

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogIn, 
  LogOut, 
  User, 
  Shield, 
  CheckCircle,
  AlertCircle,
  Loader2,
  KeyRound,
  Chrome
} from 'lucide-react';
import { googleAuthService, GoogleUser } from '../services/googleAuthService';

interface GoogleAuthButtonProps {
  onAuthSuccess?: (user: GoogleUser) => void;
  onAuthError?: (error: Error) => void;
  className?: string;
  variant?: 'default' | 'compact' | 'floating';
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
  onAuthSuccess,
  onAuthError,
  className = '',
  variant = 'default'
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<GoogleUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [authStatus, setAuthStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      setAuthStatus('checking');
      
      const authenticated = googleAuthService.isAuthenticated();
      
      if (authenticated) {
        const user = googleAuthService.getCurrentUser();
        const isValid = await googleAuthService.validateSession();
        
        if (isValid && user) {
          setIsAuthenticated(true);
          setCurrentUser(user);
          setAuthStatus('authenticated');
          console.log('✅ User authenticated:', user.email);
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
          setAuthStatus('unauthenticated');
        }
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setAuthStatus('unauthenticated');
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAuthStatus('unauthenticated');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      
      // Use OAuth2 flow (more reliable than One Tap)
      googleAuthService.initiateOAuth2Flow();
      
    } catch (error: any) {
      console.error('Sign in error:', error);
      if (onAuthError) {
        onAuthError(error);
      }
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      await googleAuthService.signOut();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAuthStatus('unauthenticated');
      setShowMenu(false);
    } catch (error: any) {
      console.error('Sign out error:', error);
      if (onAuthError) {
        onAuthError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading && authStatus === 'checking') {
    return (
      <div className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
        <span className="text-sm text-white/60">Sprawdzanie autoryzacji...</span>
      </div>
    );
  }

  // Compact variant (for header)
  if (variant === 'compact' && isAuthenticated && currentUser) {
    return (
      <div className="relative">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowMenu(!showMenu)}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300 ${className}`}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            {currentUser.picture ? (
              <img 
                src={currentUser.picture} 
                alt={currentUser.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <User className="w-4 h-4 text-white" />
            )}
          </div>
          <span className="text-sm font-medium text-white/90">{currentUser.email.split('@')[0]}</span>
          <CheckCircle className="w-4 h-4 text-green-400" />
        </motion.button>

        <AnimatePresence>
          {showMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full mt-2 right-0 w-64 p-4 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/10 shadow-2xl z-50"
            >
              <div className="space-y-3">
                <div className="pb-3 border-b border-white/10">
                  <p className="text-sm font-medium text-white">{currentUser.name}</p>
                  <p className="text-xs text-white/60">{currentUser.email}</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-left"
                >
                  <LogOut className="w-4 h-4 text-white/60" />
                  <span className="text-sm text-white/90">Wyloguj się</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Not authenticated - show sign in button
  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={variant === 'floating' ? 'fixed bottom-8 right-8 z-50' : ''}
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSignIn}
          disabled={isLoading}
          className={`
            relative group overflow-hidden
            px-6 py-3 rounded-2xl
            bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600
            text-white font-medium shadow-xl
            hover:shadow-2xl hover:shadow-blue-500/30
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-300
            ${className}
          `}
        >
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          {/* Button content */}
          <span className="relative flex items-center gap-3">
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Łączenie...</span>
              </>
            ) : (
              <>
                <Chrome className="w-5 h-5" />
                <span>Zaloguj się przez Google</span>
                <KeyRound className="w-4 h-4 opacity-60" />
              </>
            )}
          </span>
        </motion.button>

        {variant === 'default' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-200">Wymagana autoryzacja</p>
                <p className="text-xs text-amber-200/60 mt-1">
                  Zaloguj się kontem Google Ads, aby uzyskać dostęp do pełnej funkcjonalności modułu.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Authenticated - show user info (default variant)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-6 rounded-2xl bg-gradient-to-br from-green-900/20 to-emerald-900/20 backdrop-blur-xl border border-green-500/20 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-green-500/20">
            <Shield className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-green-300">Autoryzacja aktywna</p>
            <p className="text-xs text-green-300/60">Google Ads API</p>
          </div>
        </div>
        <CheckCircle className="w-6 h-6 text-green-400" />
      </div>

      {currentUser && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              {currentUser.picture ? (
                <img 
                  src={currentUser.picture} 
                  alt={currentUser.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-5 h-5 text-white" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">{currentUser.name}</p>
              <p className="text-xs text-white/60">{currentUser.email}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open('https://ads.google.com', '_blank')}
              className="flex-1 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium text-white/90"
            >
              Otwórz Google Ads
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSignOut}
              disabled={isLoading}
              className="px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 transition-colors text-sm font-medium text-red-300"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LogOut className="w-4 h-4" />
              )}
            </motion.button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default GoogleAuthButton;
